import { debounce } from './utils';

/**
 * The current cache system version
 * 
 * Would be used to flush the cache if the version is different
 * Since there might be a change in the cache structure
 * 
 * Update this version if there is a change in the cache structure
 */
const CACHE_VERSION = "v4.1";

/**
 * TTL Key
 * 
 * @type {string}
 */
const TTL_KEY = "_linguise_ttl";

/**
 * localstorage key name
 * 
 * @type {string}
 */
const LOCAL_STORAGE_KEY = "linguiseDynamicCache";

/**
 * Get the current time with TTL
 * @param {number} ttlTime The TTL time in milliseconds
 * @returns {number} Current UNIX time + TTL in seconds
 */
function nowWithTTL(ttlTime) {
    return Math.floor((new Date().getTime() + ttlTime) / 1000);
}

/**
 * @typedef {Object} TranslationCacheItem
 * Translation object from cache, mapped to key value
 *
 * @property {string} key - Key of the translation/original value
 * @property {string} value - Translated value
 * @property {number} ttl - TTL of the cache, interally in seconds
 * @property {string} [subId] - SubId of the translation
 */


/**
 * @typedef {{[key: string]: TranslationCacheItem}} TranslationCachesItem
 *
 * Wrapper for translation cache
 */

/**
 * @typedef {{[lang: string]: TranslationCachesItem}} TranslationCachesLanguages
 * 
 * Wrapper for translation cache for each language
 */

/**
 * @typedef {Object} TranslationCaches
 * Wrapper for translation cache
 * 
 * @property {TranslationCachesLanguages} caches - Array of translation cache
 * @property {number} [_linguise_ttl] - TTL of the cache
 * @property {string} [version] - Version of the cache
 */

/**
 * Make key for the cache
 * @param {string} key The key of the cache or original value
 * @param {string} [subId] The subId of the translation
 */
function makeKey(key, subId) {
    return subId ? `${subId}::${key}` : key;
}

/**
 * Cache system for the dynamic translation
 * 
 * Utilizing localStorage of each browser
 */
class TranslationCache {
    constructor() {
        /**
         * Cache object
         * 
         * @type {TranslationCachesLanguages}
         */
        this.caches = {};
        /**
         * The global TTL time in seconds
         * @type {number}
         */
        this.global_ttl = undefined;

        /**
         * TTL time for the global cache
         * 
         * @type {number}
         */
        this.MAX_TTL_TIME = 3 * 24 * 60 * 60 * 1000; // 3 days

        /** 
         * Save to storage debounced
         * 
         * Give 300ms delay between each save to not lock the localStorage and browser (a.k.a hanging)
         * from saving too many times
         * 
         * @type {() => void}
         */
        this.saveToStorageDebounced = debounce(this.saveToStorage.bind(this), 300);

        // intercept the window unload event
        // https://www.igvita.com/2015/11/20/dont-lose-user-and-app-state-use-page-visibility/
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") {
                // Immediately save the cache to the storage
                this.saveToStorage();
            }
        });
    }

    /**
     * Import cache from localStorage
     * @returns {void}
     */
    importFromStorage() {
        const localStorageCache = localStorage.getItem(LOCAL_STORAGE_KEY);

        if (!localStorageCache) {
            return;
        }

        /**
         * @type {TranslationCaches}
         */
        const parsedCache = JSON.parse(localStorageCache);

        const version = parsedCache.version;
        if (version !== CACHE_VERSION) {
            // ignore
            return;
        }

        this.caches = parsedCache.caches;
        this.global_ttl = Number(parsedCache[TTL_KEY]);
        this.updateTTL();
    }

    /**
     * Update TTL of the cache for each key add
     * 
     * If the current time is greater than the TTL, flush the cache
     * @returns {void}
     */
    updateTTL() {
        const now = Math.floor(new Date().getTime() / 1000);

        // check if invalid
        if (isNaN(this.global_ttl)) {
            this.global_ttl = nowWithTTL(this.MAX_TTL_TIME);
            this.saveToStorageDebounced();
            return;
        }

        if (now > this.global_ttl) {
            // flush cache
            this.caches = {};
        }

        // set new cache
        this.global_ttl = nowWithTTL(this.MAX_TTL_TIME);
        this.saveToStorageDebounced();
    }

    /**
     * Set the maximum TTL time
     * @param {number} ttl The TTL time in milliseconds
     */
    setMaxTTL(ttl) {
        this.MAX_TTL_TIME = ttl;
        this.updateTTL();
    }

    /**
     * Add new translation to the cache
     * @param {string} key The key of the cache or original value
     * @param {string} value The translated value
     * @param {string} lang Language code
     * @param {string} [subId] SubId of the translation
     */
    add(key, value, lang, subId) {
        if (!lang) {
            throw new Error("Language is required");
        }

        this.updateTTL();
        this.caches[lang] = this.caches[lang] || {};

        const actualKey = makeKey(key, subId);

        // check if the key already exists
        if (this.caches[lang][actualKey]) {
            // update TTL
            this.caches[lang][actualKey].value = value;
            this.caches[lang][actualKey].ttl = nowWithTTL(this.MAX_TTL_TIME);
        } else {
            this.caches[lang][actualKey] = {
                value,
                ttl: nowWithTTL(this.MAX_TTL_TIME),
            };
        }
        this.saveToStorageDebounced();
    }

    /**
     * Get the translated value from the cache if exists
     * @param {string} key key of the cache or original value
     * @param {string} lang Language code
     * @param {string} [subId] SubId of the translation
     * @returns {string|undefined}
     */
    get(key, lang, subId) {
        if (!lang) {
            throw new Error("Language is required");
        }
        const currentTime = new Date().getTime();

        if ((this.global_ttl * 1000) < currentTime) {
            // flush the cache
            this.flush();
            return undefined;
        }

        // language set
        this.caches[lang] = this.caches[lang] || {};
        const actualKey = makeKey(key, subId);

        const found = this.caches[lang][actualKey];
        if (!found) {
            return undefined;
        }

        if ((found.ttl * 1000) < currentTime) {
            // remove the cache
            try {
                delete this.caches[lang][actualKey];
            } catch {};
            // update global TTL + save
            this.updateTTL();
            return undefined;
        }

        // update TTL
        this.caches[lang][actualKey].ttl = Math.floor((currentTime + this.MAX_TTL_TIME) / 1000);
        // save to storage
        this.updateTTL();
        return found.value;
    }

    /**
     * Save the cache to localStorage
     * 
     * @returns {void}
     */
    saveToStorage() {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(
            {
                version: CACHE_VERSION,
                caches: this.caches,
                [TTL_KEY]: this.global_ttl,
            }
        ));
    }

    /**
     * Flush the cache
     * 
     * @returns {void}
     */
    flush() {
        this.caches = {};
        this.updateTTL();
    }
}

const cache = new TranslationCache();
export default cache;

window.globalLinguiseCache = cache;
